import { coreService } from './core-service'
import { Price } from '../dao/core-dao'
import { EventBusMessage } from '../util/event-bus-util'
import { logger } from '../util/logger'

export enum MetaPixelMethod {
  TRACK = 'track',
  TRACK_CUSTOM = 'trackCustom',
}

export enum MetaPixelEvent {
  LEAD = 'Lead',
  COMPLETE_REGISTRATION = 'CompleteRegistration',
  INITIATE_CHECKOUT = 'InitiateCheckout',
  START_TRIAL = 'StartTrial',
  PURCHASE = 'Purchase',
  WORKOUT_VIDEO_START = 'WorkoutVideoStarted',
  WORKOUT_VIDEO_FINISH = 'WorkoutVideoFinished',
}

export type MetaPixelMessage = {
  methodName: MetaPixelMethod
  eventName?: MetaPixelEvent
  properties?: { [key: string]: string | number }
  // Shared dedupe key. When set, the server-side Conversions API sends the same
  // eventID for this conversion so Meta collapses the browser + server pair into
  // a single event. See backend paymentService.metaCapiPurchaseEventId.
  eventId?: string
}

export const metaPixelService = {
  _push: (params: { metaPixelMessage: MetaPixelMessage }): void => {
    const { metaPixelMessage } = params
    const { methodName, eventName, properties, eventId } = metaPixelMessage
    const payload: unknown[] = [methodName, eventName]
    // fbq positional args: (method, eventName, customData, { eventID }). When an
    // eventId is present we must pass a customData object (even if empty) so the
    // options object lands in the 4th position.
    if (eventId) {
      payload.push(properties ?? {}, { eventID: eventId })
    } else if (properties) {
      payload.push(properties)
    }
    window.fbq(...payload)
  },

  sendUserCreated: (_: { message: unknown }): void => {
    const metaPixelMessage = {
      methodName: MetaPixelMethod.TRACK,
      eventName: MetaPixelEvent.LEAD,
    }
    metaPixelService._push({ metaPixelMessage })
  },

  sendRegistrationCompleted: (_: { message: unknown }): void => {
    const metaPixelMessage = {
      methodName: MetaPixelMethod.TRACK,
      eventName: MetaPixelEvent.COMPLETE_REGISTRATION,
    }
    metaPixelService._push({ metaPixelMessage })
  },

  // Fires on the click that sends the user to Stripe, not on paywall open.
  // value/currency come from the chosen plan so Meta can optimize on it.
  sendCheckoutInitiated: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { price }: { price?: Price } = message.meta ?? {}

    const metaPixelMessage = {
      methodName: MetaPixelMethod.TRACK,
      eventName: MetaPixelEvent.INITIATE_CHECKOUT,
      ...(price?.amount !== undefined ? { properties: { currency: price.currency, value: price.amount } } : {}),
    }
    metaPixelService._push({ metaPixelMessage })
  },

  /**
   * Fires on return from Stripe checkout.
   *
   * A trial subscription is `trialing` and has taken no money, so it reports
   * StartTrial with value 0 — the paid conversion is reported later, server-side,
   * off the RevenueCat trial-conversion renewal. A no-trial plan is `active`
   * immediately and reports Purchase with the real amount.
   *
   * The eventIds are deterministic and shared with the backend Conversions API
   * (see paymentService.metaCapi*EventId). The user id here is the RevenueCat
   * app_user_id, so the browser/server pair is collapsed by Meta into one event.
   */
  sendSubscriptionSet: async (_: { message: unknown }): Promise<void> => {
    const subscriptions = await coreService.getMyActiveOrTrialingSubscriptions()
    const subscription = subscriptions[0]
    const subscriptionItem = subscription?.items[0]

    if (subscriptionItem) {
      const { amount, currency } = subscriptionItem.plan
      const isTrialing = subscription.status === 'trialing'
      const me = await coreService.getMe()

      const metaPixelMessage = isTrialing
        ? {
            methodName: MetaPixelMethod.TRACK,
            eventName: MetaPixelEvent.START_TRIAL,
            properties: { currency, value: 0 },
            eventId: `starttrial_${me.id}`,
          }
        : {
            methodName: MetaPixelMethod.TRACK,
            eventName: MetaPixelEvent.PURCHASE,
            properties: { currency, value: amount },
            eventId: `purchase_${me.id}`,
          }
      metaPixelService._push({ metaPixelMessage })
    }
  },

  sendVideoPlaybackStarted: (_: { message: EventBusMessage }): void => {
    const metaPixelMessage = {
      methodName: MetaPixelMethod.TRACK_CUSTOM,
      eventName: MetaPixelEvent.WORKOUT_VIDEO_START,
    }
    metaPixelService._push({ metaPixelMessage })
  },

  sendVideoPlaybackEnded: (_: { message: EventBusMessage }): void => {
    const metaPixelMessage = {
      methodName: MetaPixelMethod.TRACK_CUSTOM,
      eventName: MetaPixelEvent.WORKOUT_VIDEO_FINISH,
    }
    metaPixelService._push({ metaPixelMessage })
  },
}
